import {
  BackendView,
  DEFAULT,
  FORECAST,
  STANDARD,
  View,
  ViewSharePermission,
  ViewSharePrincipalType,
  ViewShareRole,
  VIEW_SHARE_PRINCIPAL_TYPE,
  VIEW_SHARE_ROLE,
} from '../types/ViewTypes';
import { transformBackendViewToInternalFormat } from './backendViews';
import { VIEWS_NAMESPACE_NONE } from '../constants';
import type {
  SalesView,
  ViewPermission,
  ViewRole,
  ViewWithPermissions,
} from '../../../__generated__/chirp/com/hubspot/sales/rpc/ViewsCrudRpc';

const narrowViewType = (type: SalesView['type']): View['type'] | undefined => {
  if (type === STANDARD || type === FORECAST || type === DEFAULT) {
    return type;
  }
  return undefined;
};

// Narrows the CHIRP ViewRole (which includes the unhandledEnumCase symbol) down
// to a concrete View share role, or undefined when it is absent/unrecognized.
const narrowViewRole = (
  role: ViewRole | undefined
): ViewShareRole | undefined =>
  role === VIEW_SHARE_ROLE.OWNER ||
  role === VIEW_SHARE_ROLE.COLLABORATOR ||
  role === VIEW_SHARE_ROLE.MEMBER
    ? role
    : undefined;

const getSharedUserIdsFromPermissions = (
  permissions: ViewPermission[]
): number[] =>
  permissions
    .filter(
      (p) =>
        p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.USER &&
        p.viewRole !== VIEW_SHARE_ROLE.OWNER
    )
    .map((p) => p.principalId);

const getSharedTeamIdsFromPermissions = (
  permissions: ViewPermission[]
): number[] =>
  permissions
    .filter(
      (p) =>
        p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.TEAM &&
        p.viewRole !== VIEW_SHARE_ROLE.OWNER
    )
    .map((p) => p.principalId);

// Carries the explicit per-principal role (OWNER / COLLABORATOR / MEMBER) for
// each USER/TEAM straight off the CHIRP response so the Shared with column can
// render real roles instead of the legacy owner/member shim.
const toViewSharePermissions = (
  permissions: ViewPermission[]
): ViewSharePermission[] =>
  permissions
    .filter(
      (p) =>
        p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.USER ||
        p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.TEAM
    )
    .map((p) => ({
      principalId: p.principalId,
      principalType: p.principalType as ViewSharePrincipalType,
      role: narrowViewRole(p.viewRole) ?? VIEW_SHARE_ROLE.MEMBER,
    }));

/**
 * Maps a CHIRP getViewsByComposableSearch result (a SalesView plus its inline
 * permissions list) into the internal View shape. The JSON-config fields
 * (columns/state/filters/etc.) arrive in the same stringified shape as the v4
 * BackendView, so we reuse the existing v4 transform for those. The inline
 * permissions become `view.permissions` (the source of truth that the Owner and
 * Shared with columns render from), and the current user's role becomes
 * `view.userRole`, which drives the edit/delete permission check (the backend
 * returns OWNER for view owners and super-admins). ownerId/teamId/
 * permissionDetails are kept in their v4 shape only for the update mutation
 * payload, the legacy v4 path, and the legacy shared-with summary.
 */
export const transformViewWithPermissionsToInternalFormat = ({
  view,
  permissions = [],
  userRole,
}: ViewWithPermissions): View => {
  const backendViewLike: BackendView = {
    id: String(view.id),
    idLabel: view.idLabel,
    name: view.name ?? '',
    objectTypeId: view.objectTypeId ?? '',
    ownerId: view.ownerId,
    private: view.private ?? false,
    namespaceName: view.namespaceName ?? VIEWS_NAMESPACE_NONE,
    type: narrowViewType(view.type),
    columns: view.columns ?? '',
    state: view.state ?? '',
    filterGroups: view.filterGroups ?? '',
    quickFilters: view.quickFilters ?? '',
    quickFilterProperties: view.quickFilterProperties,
    currentViewVisualization: view.currentViewVisualization,
    viewColor: view.viewColor,
  };

  const baseView = transformBackendViewToInternalFormat(backendViewLike);

  const sharedUserIds = getSharedUserIdsFromPermissions(permissions);
  const sharedTeamIds = getSharedTeamIdsFromPermissions(permissions);
  const viewPermissions = toViewSharePermissions(permissions);

  return {
    ...baseView,
    // v4-shaped fields the update mutation payload / legacy v4 path / legacy
    // shared-with summary still read. ownerId is the view's primary owner;
    // teamId/permissionDetails reflect the non-owner shares.
    ownerId: view.ownerId,
    teamId: sharedTeamIds.length > 0 ? sharedTeamIds[0] : baseView.teamId,
    permissionDetails: { userIds: sharedUserIds },
    // Source of truth for rendering the Owner and Shared with columns: the full
    // per-principal role list straight off the composable-search response.
    permissions: viewPermissions.length > 0 ? viewPermissions : undefined,
    // The current user's role on this view drives the edit/delete check.
    userRole: narrowViewRole(userRole),
    publicDefaultRole: view.publicDefaultRole,
    // Only populated when the request set `includeLastUsed`; `0` means the
    // usage service reported nothing for this view.
    lastUsedTimestamp: view.lastUsedTimestamp,
  };
};
